import { useUser } from '@gat-ship/user'
import { useModalStack } from '@gat-ship/vue-components'
import type { SearchFunctionMap } from '@gat-ship/types'
import {
  createGlobalSearchSession,
  type GlobalSearchSession
} from '../state/global-search-session'
import {
  createRecentSearchStore,
  type RecentSearchStore
} from '../state/recent-search-storage'
import { createBuiltInSearchFunctions } from '../model/navigation-search-results'
import {
  provideGlobalSearch,
  provideRecentSearches
} from './global-search-context'

/**
 * Creates and provides the global-search session and recent-search
 * store for the dialog subtree. Runs in the dialog content's setup —
 * lazily on open — so the session resets per open and `useUser()` is
 * injected well past app bootstrap. Must run under `UserProvider`; the
 * built-in navigation provider is permission-gated.
 *
 * Also returns both stores: the providing component can't `inject()`
 * its own `provide()`.
 */
export function useProvideGlobalSearch(options: {
  searchFunctions: () => SearchFunctionMap | undefined
}): { session: GlobalSearchSession; recentSearches: RecentSearchStore } {
  const user = useUser()
  const stack = useModalStack()
  const recentSearches = createRecentSearchStore()
  const session = createGlobalSearchSession({
    searchFunctions: options.searchFunctions,
    builtInSearchFunctions: createBuiltInSearchFunctions(user, stack),
    recordRecentSearch: recentSearches.addRecentSearch
  })
  provideGlobalSearch(session)
  provideRecentSearches(recentSearches)
  return { session, recentSearches }
}
